import { ReactElement } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import {
  EventLine,
  TimelineDecoratorWrapper,
  TimelineDetailWrapper,
  TimelineEventWrapper,
  TimelineWrapper
} from 'components/v2/Timeline'
import Heading from 'components/v2/Heading'
import { DestinationIcon } from 'components/Icons/DestinationIcon'
import { checkInMessages, getTripStopStatuses, punctualityStatusMessages } from 'features/Trips/utils'
import { TripV2 } from 'gql/generated/graphql'
import { safeFormatMessage } from 'utils/intl'
import { device } from 'utils/style'
import { formatDateWithTimezone } from 'utils/date'

const TimelinePunctualityStatusWrapper = styled.div`
  align-items: flex-end;
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media ${device.tablet} {
    align-items: flex-start;
    flex-direction: row;
    justify-content: flex-end;
  }
`

const TimelinePunctualityStatusTitle = styled(Heading.Body)`
  font-weight: 500;
`

const TimelinePunctualityTimesWrapper = styled.div`
  color: #b0b0b0;
  display: flex;
  flex-direction: column;
  font-size: 14px;
  gap: 4px;
`

const TimelinePunctualityCheckInTitle = styled(Heading.Body)`
  font-size: 14px;
  font-weight: 500;
`

const TimelinePunctualityCheckInDescription = styled.div`
  color: #717171;
  font-size: 13px;
`

interface ITripDetailsTimelineProps {
  trip: TripV2
}

export const TripDetailsTimeline = ({ trip }: ITripDetailsTimelineProps): ReactElement => {
  const intl = useIntl()

  const stopStatuses = getTripStopStatuses(trip)

  return (
    <TimelineWrapper>
      {stopStatuses.map((stop, index): ReactElement => {
        const isLastEvent = index === trip.itinerary.length - 1
        const shouldShowLine = !isLastEvent

        return (
          <TimelineEventWrapper key={stop.place.id}>
            <TimelineDetailWrapper>
              <TimelinePunctualityStatusWrapper>
                <TimelinePunctualityStatusTitle>
                  {safeFormatMessage({ intl, messages: punctualityStatusMessages, key: stop.status })}
                </TimelinePunctualityStatusTitle>
                <TimelinePunctualityTimesWrapper>
                  {stop.arrivalTime && (
                    <>
                      <span>
                        <FormattedMessage id="trips.drawer.details.arrival.label" defaultMessage="Arrival" />
                      </span>
                      <span>{stop.arrivalTime}</span>
                    </>
                  )}
                  {stop.departureTime && (
                    <>
                      <span>
                        <FormattedMessage id="trips.drawer.details.departure.label" defaultMessage="Departure" />
                      </span>
                      <span>{stop.departureTime}</span>
                    </>
                  )}
                </TimelinePunctualityTimesWrapper>
              </TimelinePunctualityStatusWrapper>
            </TimelineDetailWrapper>
            <TimelineDecoratorWrapper>
              {stop.isDestination ? <DestinationIcon /> : <EventLine color="#5d59d7" showLine={shouldShowLine} />}
            </TimelineDecoratorWrapper>
            <TimelineDetailWrapper>
              <TimelinePunctualityCheckInTitle>
                {stop.isOrigin && (
                  <FormattedMessage
                    id="trips.drawer.details.origin"
                    defaultMessage="Origin: {origin}"
                    values={{ origin: stop.place.name }}
                  />
                )}
                {stop.isDestination && (
                  <FormattedMessage
                    id="trips.drawer.details.destination"
                    defaultMessage="Destination: {destination}"
                    values={{ destination: stop.place.name }}
                  />
                )}
                {!stop.isOrigin && !stop.isDestination && (
                  <FormattedMessage
                    id="trips.drawer.details.stop"
                    defaultMessage="Stop {index}: {stop}"
                    values={{ index: index + 1, stop: stop.place.name }}
                  />
                )}
              </TimelinePunctualityCheckInTitle>
              {stop.checkIns.map(
                (checkIn, index): ReactElement => (
                  <TimelinePunctualityCheckInDescription key={`checkIn-${stop.place.id}-${index}`}>
                    <FormattedMessage
                      id="trips.drawer.details.destination"
                      defaultMessage="Check in: <strong>{method}</strong> - {time}"
                      values={{
                        strong: (value) => <strong>{value}</strong>,
                        method: safeFormatMessage({ intl, messages: checkInMessages, key: checkIn.method }),
                        time: formatDateWithTimezone(checkIn.at, stop.place.timezone)
                      }}
                    />
                  </TimelinePunctualityCheckInDescription>
                )
              )}
            </TimelineDetailWrapper>
          </TimelineEventWrapper>
        )
      })}
    </TimelineWrapper>
  )
}
